import { useCallback, useEffect, useState } from 'react';
import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos, ResumoQueFiltra,
} from '../BarraDeLista';
import { api } from '../api';
import { data, dataHora } from '../formato';
import { useSessao } from '../sessao';
import {
  Area, Aviso, Carregando, Dialogo, ErroDaApi, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';

/**
 * As contas dos métodos — a porta que faltava para a `RN-121`.
 *
 * ── Por que esta tela existe ───────────────────────────────────────────
 *
 * A regra está no banco desde a `0001`: aprovar um ensaio exige a versão
 * de cálculo congelada, para que o laudo diga com QUAL conta o número
 * saiu. Correta, e até a `0087` impossível de satisfazer — a tabela
 * estava vazia e não havia rota, tela nem comando que criasse uma linha.
 *
 * O efeito, medido: o PRIMEIRO ensaio de CADA método caminhava inteiro —
 * programado, executado, lançado, conferido, verificado por outra pessoa
 * — e morria no clique de aprovar. No pior momento possível, com o
 * cliente esperando, e sem saída nenhuma.
 *
 * ── O que esta tela NÃO faz ────────────────────────────────────────────
 *
 * Não aprova sozinha, e a migração também não aprovou. Uma versão de
 * cálculo é uma afirmação assinada — «esta é a conta que a Testesolo
 * reconhece como sua» —, e o §25 diz quem assina. A proposta fica pronta
 * na mesa com a norma e a planilha real contra a qual a conta confere; a
 * assinatura é de quem clica.
 */

/** Uma linha de `vw_metodo_prontidao` (0200, RN-202). */
interface Linha {
  metodo_id: string;
  codigo: string;
  nome: string;
  classe: string | null;
  escopo: string | null;
  tem_ficha: boolean;
  versao_id: string | null;
  versao: number | null;
  versao_assinada_em: string | null;
  versao_assinada_por: string | null;
  laudos_com_a_versao: number;
  tem_proposta: boolean;
  norma: string | null;
  norma_situacao: string | null;
  exige_competencia: boolean;
  competentes: number;
  frente_codigo: string | null;
  frente_nome: string | null;
  ensaios_a_caminho: number;
  situacao: Situacao;
  /** 0221: há versão vigente, mas a conta que o sistema faz hoje é outra. */
  proposta_nova?: boolean;
  /** 312: «campo» é o método que não vai à bancada (o CPTU, 0254). */
  categoria?: string;
}

type Situacao = 'pronto' | 'falta_assinar' | 'proposta_nova' | 'sem_conta'
  | 'sem_competente' | 'sem_frente' | 'sem_ficha';

interface Versao {
  id: string;
  versao: number;
  definicao: Record<string, unknown>;
  suite_teste_ref: string;
  vigencia_inicio: string;
  vigencia_fim: string | null;
  vigente: boolean;
  aprovado_por_nome: string | null;
  data_aprovacao: string;
  ensaios_aprovados_com_ela: number;
}

interface Proposta {
  definicao: Record<string, unknown>;
  suite_teste_ref: string;
  resumo: string;
  origem: string;
}

const SITUACAO: Record<Situacao, { texto: string; tom: Tom; diz: string }> = {
  pronto: { texto: 'Pronto', tom: 'ok',
    diz: 'Roda do começo ao laudo: tem ficha, conta oficial assinada e quem execute.' },
  falta_assinar: { texto: 'Falta assinar a conta', tom: 'atencao',
    diz: 'Lança e calcula, mas o ensaio é recusado no aprovar (RN-121): '
      + 'a proposta está pronta, falta o responsável técnico assinar.' },
  proposta_nova: { texto: 'Conta nova para assinar', tom: 'atencao',
    diz: 'Há uma versão assinada, mas o sistema passou a calcular mais do '
      + 'que ela cobre (ex.: a sedimentação na granulometria). O que ela não '
      + 'cobre fica travado até a versão nova ser assinada.' },
  sem_conta: { texto: 'Sem conta', tom: 'critico',
    diz: 'Tem ficha e não tem conta escrita para assinar — vale abrir um relato.' },
  sem_competente: { texto: 'Ninguém competente', tom: 'critico',
    diz: 'O método exige competência e nenhuma pessoa ativa tem competência vigente.' },
  sem_frente: { texto: 'Sem frente', tom: 'atencao',
    diz: 'Ensaio trivial sem frente de serviço: a programação não tem onde pô-lo (RN-199).' },
  sem_ficha: { texto: 'Sem ficha', tom: 'neutro',
    diz: 'Ainda não há tela de lançamento: pode ser programado e medido, não lançado.' },
};

type Recorte = 'lancam' | 'pronto' | 'falta_assinar' | 'outros' | 'sem_ficha' | 'todos';

const NO_RECORTE: Record<Recorte, (l: Linha) => boolean> = {
  lancam: (l) => l.tem_ficha,
  pronto: (l) => l.situacao === 'pronto',
  falta_assinar: (l) => l.situacao === 'falta_assinar'
    || l.situacao === 'proposta_nova',
  outros: (l) => ['sem_conta', 'sem_competente', 'sem_frente'].includes(l.situacao),
  sem_ficha: (l) => l.situacao === 'sem_ficha',
  todos: () => true,
};

/** O que a proposta afirma, em prosa e não em JSON cru. */
function Definicao({ d }: { d: Record<string, unknown> }) {
  const lista = (chave: string) => {
    const v = d[chave];
    return Array.isArray(v) ? (v as unknown[]).map(String) : [];
  };
  const norma = typeof d.norma === 'string' ? d.norma : null;
  const validado = lista('validado_contra');
  const onde = lista('onde');
  const naoInclui = lista('nao_inclui');

  return (
    <dl className="definicao">
      {norma && (<><dt>Norma</dt><dd>{norma}</dd></>)}
      {validado.length > 0 && (
        <><dt>Conferida contra</dt>
          <dd>{validado.map((v) => <div key={v}>{v}</div>)}</dd></>)}
      {onde.length > 0 && (
        <><dt>Implementada em</dt>
          <dd className="sub">{onde.join(' · ')}</dd></>)}
      {naoInclui.length > 0 && (
        <><dt>Não inclui</dt>
          <dd>{naoInclui.map((v) => <div key={v}>{v}</div>)}</dd></>)}
    </dl>
  );
}

function Ficha({ metodo, aoFechar, aoAssinar }: {
  metodo: Linha;
  aoFechar: () => void;
  aoAssinar: () => void;
}) {
  const { pode } = useSessao();
  const [dados, setDados] =
    useState<{ versoes: Versao[]; proposta: Proposta | null } | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [motivo, setMotivo] = useState('');
  const [gravando, setGravando] = useState(false);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const r = await api<{ versoes: Versao[]; proposta: Proposta | null }>(
          `/normas/metodos/${metodo.metodo_id}/calculo`);
        if (vivo) setDados(r);
      } catch (e) { if (vivo) setErro(e); }
    })();
    return () => { vivo = false; };
  }, [metodo.metodo_id]);

  const assinar = async () => {
    setErro(null);
    setGravando(true);
    try {
      await api(`/normas/metodos/${metodo.metodo_id}/calculo`, {
        metodo: 'POST', corpo: { motivo },
      });
      aoAssinar();
      aoFechar();
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  const podeAssinar = pode('metodo.aprovar_calculo');

  return (
    <Dialogo aberto titulo={`${metodo.codigo} — a conta oficial`}
      aoFechar={aoFechar}>
      {erro ? <ErroDaApi erro={erro} /> : null}
      {!dados ? <Carregando /> : (
        <>
          {dados.versoes.length > 0 && (
            <div className="rola" style={{ marginBottom: '1rem' }}>
              <table>
                <thead>
                  <tr>
                    <th scope="col" className="num">Versão</th>
                    <th scope="col">Assinada por</th>
                    <th scope="col" className="num">Laudos</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.versoes.map((v) => (
                    <tr key={v.id}>
                      <th scope="row" className="num">{v.versao}</th>
                      <td>
                        {v.aprovado_por_nome ?? '—'}
                        <span className="sub">{dataHora(v.data_aprovacao)}</span>
                        <span className="sub">{v.suite_teste_ref}</span>
                      </td>
                      <td className="num">{v.ensaios_aprovados_com_ela}</td>
                      <td>
                        <Selo tom={v.vigente ? 'ok' : 'neutro'}>
                          {v.vigente ? 'Vigente' : 'Encerrada'}
                        </Selo>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {dados.proposta ? (
            <>
              <h3>Proposta na mesa</h3>
              <p>{dados.proposta.resumo}</p>
              <Definicao d={dados.proposta.definicao} />
              <p className="sub">
                Suíte que a prova: <code>{dados.proposta.suite_teste_ref}</code>
              </p>

              {podeAssinar ? (
                <>
                  <Area
                    rotulo="Por que esta conta passa a ser a oficial"
                    dica="Fica na trilha para sempre, junto com o seu nome."
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                  <div className="rodape-dialogo">
                    <button type="button" className="secundario"
                      onClick={aoFechar}>Cancelar</button>
                    <button type="button"
                      disabled={motivo.trim().length < 5 || gravando}
                      onClick={() => { void assinar(); }}>
                      {gravando ? 'Assinando…' : 'Assinar como conta oficial'}
                    </button>
                  </div>
                </>
              ) : (
                <p className="rodape-regra">
                  Assinar a conta de um método é ato de responsabilidade
                  técnica. Seu perfil não tem <code>metodo.aprovar_calculo</code>
                  {' '}— quem tem é o responsável técnico.
                </p>
              )}
            </>
          ) : (
            <p className="rodape-regra">
              <strong>Não há proposta pronta para este método.</strong>{' '}
              {metodo.tem_ficha
                ? 'Ele tem ficha de lançamento, e mesmo assim ninguém '
                  + 'escreveu a conta dele aqui — vale abrir um relato.'
                : 'Ele ainda não tem ficha de lançamento: não existe conta '
                  + 'implementada para reconhecer. Enquanto isso, ele pode '
                  + 'ser programado e medido, mas não aprovado.'}
            </p>
          )}
        </>
      )}
    </Dialogo>
  );
}

/**
 * ── O retrato de todos os métodos (0200, RN-202) ───────────────────────
 *
 * «Hoje no SGQ não consigo saber quais ensaios estão homologados e que
 * consigo rodar ensaio» — Humberto, 28/09/2026.
 *
 * Até a 213 esta tela listava só o que FALTAVA assinar: o método assinado
 * sumia, e não havia lugar que dissesse o que o laboratório já roda do
 * começo ao laudo. Agora ela mostra todos, com as quatro respostas que
 * decidem isso — ficha, conta oficial, gente competente, frente — e a
 * primeira coisa que falta.
 */
export function Calculos() {
  const [linhas, setLinhas] = useState<Linha[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [aberto, setAberto] = useState<Linha | null>(null);
  const [recorte, setRecorte] = useState<Recorte>('lancam');
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setErro(null);
    try { setLinhas(await api<Linha[]>('/normas/metodos/prontidao')); }
    catch (e) { setErro(e); }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  const todas = linhas ?? [];
  const conta = (r: Recorte) => todas.filter(NO_RECORTE[r]).length;
  const termo = busca.trim().toLowerCase();
  const visiveis = todas
    .filter(NO_RECORTE[recorte])
    .filter((l) => !termo
      || l.codigo.toLowerCase().includes(termo)
      || l.nome.toLowerCase().includes(termo));
  const urgentes = todas.filter(
    (l) => (l.situacao === 'falta_assinar' || l.situacao === 'proposta_nova')
      && l.ensaios_a_caminho > 0);


  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Métodos homologados</h1>
          <p className="sub">
            O que o laboratório roda do começo ao laudo — e o que falta nos
            outros. Por código do método.
          </p>
        </div>
      </div>

      {urgentes.length > 0 && (
        <Aviso tipo="atencao" titulo={
          `${urgentes.length} método(s) com ensaio a caminho e sem conta `
          + 'assinada'}>
          <p>
            {urgentes.map((l) => l.codigo).join(', ')}: esses ensaios vão ser
            recusados no clique de aprovar (RN-121). A proposta de cada um
            já está pronta — assinar custa um minuto.
          </p>
        </Aviso>
      )}

      {/*
        * O resumo que filtra do padrão (onda 273), no lugar dos
        * indicadores próprios desta tela: os mesmos quatro recortes, o
        * mesmo componente de todas as listas.
        */}
      {linhas && (
        <ResumoQueFiltra rotulo="Situação dos métodos"
          ativo={['pronto', 'falta_assinar', 'outros', 'sem_ficha'].includes(recorte) ? recorte : null}
          aoEscolher={(k) => setRecorte((k as Recorte | null) ?? 'lancam')}
          itens={[
            { chave: 'pronto', numero: conta('pronto'), rotulo: 'Prontos', aux: 'rodam e aprovam', tom: 'ok' },
            { chave: 'falta_assinar', numero: conta('falta_assinar'), rotulo: 'Falta assinar', aux: 'rodam, mas a conta não está assinada', tom: 'atencao' },
            { chave: 'outros', numero: conta('outros'), rotulo: 'Incompletos', aux: 'sem conta, sem gente ou sem frente', tom: 'critico' },
            { chave: 'sem_ficha', numero: conta('sem_ficha'), rotulo: 'Sem ficha', aux: 'ainda sem ficha de lançamento', tom: 'neutro' },
          ]} />
      )}

      <BarraDeLista rotulo="Filtros dos métodos">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Código ou nome do método" rotulo="Buscar método" />
        <AlternarDaBarra ligado={recorte === 'lancam'}
          aoMudar={(v) => setRecorte(v ? 'lancam' : 'todos')}>
          {recorte === 'lancam' ? `✓ Só os que se lançam (${conta('lancam')})` : `Todos os métodos (${todas.length})`}
        </AlternarDaBarra>
      </BarraDeLista>
      <FiltrosAtivos
        itens={busca ? [{ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') }] : []}
        aoLimpar={() => setBusca('')}
        contagem={linhas ? `${visiveis.length} ${visiveis.length === 1 ? 'método' : 'métodos'} · ordem: código` : undefined} />

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : (
          <div className="cartao">
            {visiveis.length === 0 ? (
              <Vazio>Nenhum método neste recorte.</Vazio>
            ) : (
              <div className="rola">
                <table className="dados" data-tabela="prontidao">
                  <thead>
                    <tr>
                      <th scope="col">Método</th>
                      <th scope="col" className="col-opcional">Norma</th>
                      <th scope="col">Conta oficial</th>
                      <th scope="col" className="num col-opcional">Competentes</th>
                      <th scope="col" className="col-opcional">Frente</th>
                      <th scope="col" className="num col-opcional">A caminho</th>
                      <th scope="col">Situação</th>
                      <th scope="col" aria-label="Ações" />
                    </tr>
                  </thead>
                  <tbody>
                    {visiveis.map((l) => (
                      <tr key={l.metodo_id} data-metodo={l.codigo}
                        data-situacao={l.situacao}>
                        <th scope="row">
                          <strong>{l.codigo}</strong>
                          <span className="sub">{l.nome}</span>
                        </th>
                        <td className="col-opcional sub">{l.norma ?? '—'}</td>
                        <td>
                          {l.versao_id ? (
                            <>
                              <strong>Versão {l.versao}</strong>
                              <span className="sub">
                                {l.versao_assinada_por ?? '—'} ·{' '}
                                {data(l.versao_assinada_em)}
                              </span>
                            </>
                          ) : l.tem_proposta ? (
                            <span className="sub">Proposta na mesa</span>
                          ) : <span className="nada">—</span>}
                        </td>
                        <td className="num col-opcional">
                          {l.exige_competencia
                            ? (l.competentes > 0
                                ? l.competentes
                                : <strong>0</strong>)
                            : <span className="nada" title="O método não exige competência">não exige</span>}
                        </td>
                        {/* 312: o método de campo (CPTU) não tem frente nem
                            máquina de laboratório — é do campo. */}
                        <td className="col-opcional sub" data-coluna="frente">
                          {l.frente_codigo ?? (l.categoria === 'campo' ? 'campo'
                            : l.classe === 'trivial' ? '—' : 'máquina')}
                        </td>
                        <td className="num col-opcional">
                          {l.ensaios_a_caminho > 0
                            ? l.ensaios_a_caminho
                            : <span className="nada">—</span>}
                        </td>
                        <td>
                          <Selo tom={SITUACAO[l.situacao].tom}
                            titulo={SITUACAO[l.situacao].diz}>
                            {SITUACAO[l.situacao].texto}
                          </Selo>
                        </td>
                        <td>
                          <button type="button" className="botao discreto"
                            onClick={() => setAberto(l)}>
                            {l.situacao === 'falta_assinar'
                              || l.situacao === 'proposta_nova'
                              ? 'Assinar' : 'Abrir'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="rodape-regra">
              <strong>Pronto</strong> quer dizer: tem ficha de lançamento, a
              conta oficial está assinada (sem ela o ensaio é recusado no
              aprovar — RN-121), há ao menos uma pessoa ativa com competência
              vigente quando o método a exige, e o trivial tem frente de
              serviço (RN-199). Assinar a conta é do responsável técnico, com
              o próprio nome — o sistema só deixa a proposta pronta.
            </p>
          </div>
        )}

      {aberto && (
        <Ficha metodo={aberto} aoFechar={() => setAberto(null)}
          aoAssinar={() => { void carregar(); }} />
      )}
    </div>
  );
}
